import {
 useState, useEffect, useCallback, useMemo,
} from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Rnd } from 'react-rnd';
import {
  Card,
  CardContent,
  Typography,
  CardActions,
  IconButton,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Menu,
  MenuItem,
  CardMedia,
  Tooltip,
  CircularProgress,
  Box,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { usePremiumTokens } from '../theme/usePremiumTokens';
import { typography, radius, transitions } from '../theme/premiumTokens';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PendingIcon from '@mui/icons-material/Pending';
import RouteIcon from '@mui/icons-material/Route';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AnchorIcon from '@mui/icons-material/Anchor';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

import { useTranslation } from './LocalizationProvider';
import { prefixString } from '../util/stringUtils';
import RemoveDialog from './RemoveDialog';
import PositionValue from './PositionValue';
import {
 useDeviceReadonly, useAdministrator, useManager, useLimitCommand, useRestriction 
} from '../util/permissions';
import usePositionAttributes from '../attributes/usePositionAttributes';
import { devicesActions, errorsActions } from '../../store';
import { useCatch, useCatchCallback } from '../../reactHelper';
import { useAttributePreference } from '../util/preferences';
import fetchOrThrow from '../util/fetchOrThrow';
import { deviceHasCamera } from '../util/cameras';
import { getStatusColor } from '../../common/util/formatter';

// Custom Dialogs
import MultiActionDialog from './MultiActionDialog';
import AnchorDialog from './AnchorDialog';
import RemoveAnchorDialog from './RemoveAnchorDialog';
import LockUnlockConfirmationDialog from './confirmation-dialog';
import { SuccessToast } from './success-toast';

// Audio functions
const playBlockSound = () => {
  try {
    const audio = new Audio('./resources/bloqueio.wav');
    audio.volume = 0.5;
    audio.play().catch(() => {
      const fallbackAudio = new Audio('./src/resources/bloqueio.wav');
      fallbackAudio.volume = 0.5;
      fallbackAudio.play().catch(console.warn);
    });
  } catch (error) {
    console.warn('Erro no áudio de bloqueio:', error);
  }
};

const playUnblockSound = () => {
  try {
    const audio = new Audio('./resources/desbloqueio.wav');
    audio.volume = 0.5;
    audio.play().catch(() => {
      const fallbackAudio = new Audio('./src/resources/desbloqueio.wav');
      fallbackAudio.volume = 0.5;
      fallbackAudio.play().catch(console.warn);
    });
  } catch (error) {
    console.warn('Erro no áudio de desbloqueio:', error);
  }
};

let count = 0;

const useStyles = makeStyles()((theme, { desktopPadding, tokens }) => ({
  // 2ECOMP Premium: Card com glassmorphism
  card: {
    pointerEvents: 'auto',
    width: theme.dimensions.popupMaxWidth,
    // Glassmorphism heavy - uses tokens for dark/light mode
    backgroundColor: tokens?.glassmorphism?.heavy?.background || 'rgba(22, 27, 34, 0.9)',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    border: `1px solid ${tokens?.border?.default || 'rgba(255, 255, 255, 0.12)'}`,
    borderRadius: radius.xl,
    boxShadow: `${tokens?.shadows?.xl || '0 16px 48px rgba(0, 0, 0, 0.6)'}, ${tokens?.shadows?.inset || 'inset 0 1px 0 rgba(255, 255, 255, 0.05)'}, 0 0 0 1px ${tokens?.border?.subtle || 'rgba(255,255,255,0.04)'}`,
    overflow: 'hidden',
    // Animação de entrada
    animation: 'scaleIn 0.2s ease-out',
    // Borda accent no topo
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 12,
      right: 12,
      height: 2,
      background: `linear-gradient(90deg, transparent, ${tokens.accent.primary}, transparent)`,
      borderRadius: '0 0 2px 2px',
      zIndex: 10,
    },
  },
  // 2ECOMP Premium: Media container
  media: {
    height: theme.dimensions.popupImageHeight,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
    position: 'relative',
    cursor: 'pointer',
    borderRadius: `${radius.lg} ${radius.lg} 0 0`,
    overflow: 'hidden',
  },
  mediaButton: {
    color: theme.palette.common.white,
    mixBlendMode: 'difference',
  },
  dragHandle: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '40px',
    zIndex: 10,
    cursor: 'move',
  },
  // 2ECOMP Premium: Header com tipografia Inter
  imageHeader: {
    textShadow: '0 2px 8px rgba(0,0,0,0.8)',
    position: 'absolute',
    top: '12px',
    left: '12px',
    zIndex: 2,
    pointerEvents: 'none',
  },
  // 2ECOMP Premium: Botão fechar com hover accent
  imageCloseButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 11,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(8px)',
    border: `1px solid ${tokens.border.subtle}`,
    transition: transitions.all,
    '&:hover': {
      backgroundColor: tokens.accent.subtle,
      borderColor: tokens.accent.primary,
    },
  },
  videoOverlay: {
    position: 'relative',
    width: '100%',
    minHeight: 220,
    maxHeight: 300,
    zIndex: 5,
    backgroundColor: '#000',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: `${radius.lg} ${radius.lg} 0 0`,
  },
  // 2ECOMP Premium: Header sem imagem
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2, 1, 2.5),
    background: tokens.surface.glass,
    borderBottom: `1px solid ${tokens.border.subtle}`,
  },
  // 2ECOMP Premium: Content area
  content: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
    maxHeight: theme.dimensions.cardContentMaxHeight,
    overflow: 'auto',
    background: 'transparent',
  },
  icon: {
    width: '25px',
    height: '25px',
    filter: 'brightness(0) invert(1)',
  },
  // 2ECOMP Premium: Table com tipografia refinada
  table: {
    '& .MuiTableCell-sizeSmall': {
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: theme.spacing(0.4),
      paddingBottom: theme.spacing(0.4),
    },
    '& .MuiTableCell-sizeSmall:first-of-type': {
      paddingRight: theme.spacing(1.5),
    },
  },
  // 2ECOMP Premium: Cell sem borda
  cell: {
    borderBottom: 'none',
    lineHeight: 1.3,
  },
  // 2ECOMP Premium: Actions com glass hover
  actions: {
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 1.5),
    borderTop: `1px solid ${tokens.border.subtle}`,
    background: tokens.surface.glass,
    '& .MuiIconButton-root': {
      color: tokens.text.secondary,
      transition: transitions.all,
      borderRadius: radius.md,
      '&:hover': {
        color: tokens.accent.primary,
        backgroundColor: tokens.accent.subtle,
      },
    },
  },
  eventBadge: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(1),
  },
  eventChip: {
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    boxShadow: '0 2px 8px rgba(211, 47, 47, 0.35)',
    animation: 'pulse 2s infinite',
    '@keyframes pulse': {
      '0%': {
        boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.4)',
      },
      '70%': {
        boxShadow: '0 0 0 6px rgba(211, 47, 47, 0)',
      },
      '100%': {
        boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)',
      },
    },
  },
  root: {
    pointerEvents: 'none',
    position: 'fixed',
    zIndex: 5,
    left: '50%',
    [theme.breakpoints.up('md')]: {
      left: `calc(50% + ${desktopPadding} / 2)`,
      bottom: theme.spacing(3),
    },
    [theme.breakpoints.down('md')]: {
      left: '50%',
      bottom: `calc(${theme.spacing(3)} + ${theme.dimensions.bottomBarHeight}px)`,
    },
    transform: 'translateX(-50%)',
  },
  // Quando controlado externamente (wrapper define posição)
  rootExternal: {
    pointerEvents: 'none',
  },
}));

// 2ECOMP Premium: StatusRow com tipografia refinada
const StatusRow = ({ name, content, tokens }) => (
  <TableRow>
    <TableCell sx={{ borderBottom: 'none', lineHeight: 1.3 }}>
      {/* Label: uppercase, muted, letter-spacing */}
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.68rem',
          fontWeight: 500,
          color: tokens.text.muted,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {name}
      </Typography>
    </TableCell>
    <TableCell sx={{ borderBottom: 'none', lineHeight: 1.3 }}>
      {/* Value: JetBrains Mono, primary color */}
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.78rem',
          fontWeight: 500,
          fontFamily: typography.fontFamily.mono,
          color: tokens.text.primary,
        }}
      >
        {content}
      </Typography>
    </TableCell>
  </TableRow>
);

const StatusCard = ({ deviceId, position, onClose, disableActions, desktopPadding = 0, useExternalPosition = false, event = null }) => {
  const theme = useTheme();
  const tokens = usePremiumTokens();
  const { classes } = useStyles({ desktopPadding, tokens });
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const t = useTranslation();

  const readonly = useRestriction('readonly');
  const deviceReadonly = useDeviceReadonly();
  const isAdmin = useAdministrator();
  const isManager = useManager();
  const limitCommands = useLimitCommand();

  // 2ECOMP: shallowEqual evita re-renders desnecessários
  const groups = useSelector((state) => state.groups.items, shallowEqual);
  const shareDisabled = useSelector((state) => state.session.server.attributes.disableShare);
  const user = useSelector((state) => state.session.user);
  const revenda = useSelector((state) => state.session.revenda);
  const device = useSelector((state) => state.devices.items[deviceId]);

  // Detectar se dispositivo tem câmera (atributo manual ou auto-detectado via videoAlarm)
  const hasCamera = deviceHasCamera(device, position);

  const deviceImage = device?.attributes?.deviceImage;
  const statusKey = getStatusColor(device?.status);
  
  // ✅ CORREÇÃO 1: Usar hasAnchor ao invés de anchor
  const hasAnchor = Boolean(
    device?.attributes?.hasAnchor && 
    device.attributes.hasAnchor !== '' && 
    device.attributes.hasAnchor !== 'null' && 
    device.attributes.hasAnchor !== null
  );
  
  const hasValidGroup = device?.groupId > 0 && groups[device.groupId]?.name;

  const positionAttributes = usePositionAttributes(t);
  const positionItems = useAttributePreference('positionItems', 'fixTime,address,speed,totalDistance');

  // Formatar nome do evento para exibição
  const eventLabel = useMemo(() => {
    if (!event || !event.type) return null;
    return t(prefixString('event', event.type)) || event.type;
  }, [event, t]);

  const navigationAppLink = useAttributePreference('navigationAppLink');
  const navigationAppTitle = useAttributePreference('navigationAppTitle');

  const [anchorEl, setAnchorEl] = useState(null);
  const [streetView, setStreetView] = useState(false);

  /* === CÓDIGO ANTIGO: Estados do video embutido (agora navega para /camera/:deviceId) ===
  const [showVideo, setShowVideo] = useState(false);
  const defaultChannel = device?.attributes?.defaultCameraChannel || 1;
  const [channel, setChannel] = useState(defaultChannel);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [retry, setRetry] = useState(0);
  const [availableChannels, setAvailableChannels] = useState([]);
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);
  === FIM === */

  // Lock/Unlock states
  const [commandLoading, setCommandLoading] = useState(false);
  const [openLockUnlockDialog, setOpenLockUnlockDialog] = useState(false);
  const [sended, setSended] = useState(null);

  // Helper function to get blocked state
  const getBlockedState = useCallback(() => {
    if (!position?.attributes) return false;

    const blockedValue = position.attributes.blocked;

    if (typeof blockedValue === 'boolean') {
      return blockedValue;
    }
    if (typeof blockedValue === 'string') {
      return blockedValue.toLowerCase() === 'true' || blockedValue === '1';
    }
    if (typeof blockedValue === 'number') {
      return blockedValue === 1;
    }

    return false;
  }, [position]);

  const isBlocked = getBlockedState();

  // Dialog states
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('delete');
  const [openAnchorDialog, setOpenAnchorDialog] = useState(false);
  const [openRemoveAnchorDialog, setOpenRemoveAnchorDialog] = useState(false);

  // Toast states
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState('success');
  const [toastOpen, setToastOpen] = useState(false);

  // Active panel state
  const [activePanel, setActivePanel] = useState(0);

  const handleRemove = useCatch(async (removed) => {
    if (removed) {
      const response = await fetchOrThrow('/api/devices');
      dispatch(devicesActions.refresh(await response.json()));
    }
    setOpenRemoveDialog(false);
  });

  const handleGeofence = useCatchCallback(async () => {
    const newItem = {
      name: t('sharedGeofence'),
      area: `CIRCLE (${position.latitude} ${position.longitude}, 50)`,
    };
    const response = await fetchOrThrow('/api/geofences', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newItem),
    });
    const item = await response.json();
    await fetchOrThrow('/api/permissions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceId: position.deviceId, geofenceId: item.id }),
    });
    navigate(`/settings/geofence/${item.id}`);
  }, [navigate, position, t]);

  // === NOVA IMPLEMENTAÇÃO: Navegar para página de câmera ===
  const handleCameraClick = useCallback(() => {
    if (device?.id) {
      navigate(`/camera/${device.id}`);
    }
  }, [device?.id, navigate]);

  /* === CÓDIGO ANTIGO COMENTADO (embutir vídeo no StatusCard) ===
  const handleCameraClickOld = useCallback(async () => {
    console.log('[Camera] Click - showVideo:', showVideo, 'device:', device?.id, device?.name, 'IMEI:', device?.uniqueId);

    if (!showVideo && device) {
      // Abre o player com canal padrão do atributo do dispositivo
      // MettaX gerencia os comandos JT1078 automaticamente
      setAvailableChannels([1, 2, 3, 4]); // Canais típicos MettaX
      setChannel(defaultChannel); // Usa canal configurado no dispositivo
      setShowVideo(true);
      setLoadingVideo(true);

      console.log('[Camera] Opening MettaX player for IMEI:', device.uniqueId, 'defaultChannel:', defaultChannel);
    } else {
      // Fecha o player
      setShowVideo(false);
      setLoadingVideo(false);
      setAvailableChannels([]);
      console.log('[Camera] Closing MettaX player');
    }
  }, [showVideo, device, defaultChannel]);
  === FIM DO CÓDIGO ANTIGO === */

  const handleLockUnlockClick = useCallback(() => {
    if (commandLoading) return;
    setOpenLockUnlockDialog(true);
  }, [commandLoading]);

  const executeLockUnlock = useCallback(async () => {
    if (commandLoading) return;

    setCommandLoading(true);

    const currentBlockedState = getBlockedState();
    const commandType = currentBlockedState ? 'engineResume' : 'engineStop';

    if (!position?.deviceId && !deviceId) {
      setToastMessage('Erro: ID do dispositivo não encontrado');
      setToastSeverity('error');
      setToastOpen(true);
      setShowSuccessToast(true);
      setCommandLoading(false);
      return;
    }

    try {
      const requestBody = {
        deviceId: position?.deviceId || deviceId,
        type: commandType,
      };

      const response = await fetch('/api/commands/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        setSended(response.status);

        if (commandType === 'engineStop') {
          setToastMessage(t('deviceBlocked') || 'Comando de bloqueio enviado com sucesso!');
          playBlockSound();
        } else {
          setToastMessage(t('deviceUnblocked') || 'Comando de desbloqueio enviado com sucesso!');
          playUnblockSound();
        }

        setToastSeverity('success');
        setToastOpen(true);
        setShowSuccessToast(true);

        // Refresh devices after command sent
        const refreshAttempts = [1000, 3000, 5000];
        refreshAttempts.forEach((delay) => {
          setTimeout(async () => {
            try {
              const refreshResponse = await fetch('/api/devices');
              if (refreshResponse.ok) {
                const devices = await refreshResponse.json();
                dispatch(devicesActions.refresh(devices));
              }
            } catch (error) {
              console.error('Error refreshing devices:', error);
            }
          }, delay);
        });
      } else {
        throw new Error('Falha ao enviar comando');
      }
    } catch (error) {
      console.error('Erro ao executar bloqueio/desbloqueio:', error);
      setToastMessage(error.message || 'Erro ao executar comando');
      setToastSeverity('error');
      setShowSuccessToast(true);
      dispatch(errorsActions.push(error.message || 'Erro ao executar comando'));
    } finally {
      setCommandLoading(false);
      setOpenLockUnlockDialog(false);
    }
  }, [commandLoading, getBlockedState, position, deviceId, t, dispatch]);

  const handleDialogResult = useCatch(async (confirmed) => {
    setDialogOpen(false);
    if (confirmed) {
      try {
        const response = await fetch('/api/devices');
        const devices = await response.json();
        dispatch(devicesActions.refresh(devices));

        if (dialogType === 'delete' && onClose) {
          onClose();
        }
      } catch (err) {
        setToastMessage('Erro ao executar ação');
        setToastSeverity('error');
        setShowSuccessToast(true);
      }
    }
  });

  // Handler para clique no CardMedia (Street View)
  const handleStreetViewClick = useCallback(() => {
    if (position) {
      window.open(
        `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${position.latitude}%2C${position.longitude}&heading=${position.course || 0}`,
        '_blank'
      );
    }
  }, [position]);

  // Street View validation effect
  useEffect(() => {
    if (position) {
      fetch(
        `https://street-view.entrack-plataforma.workers.dev/?heading=${position.course || 0}&location=${position.latitude},${position.longitude}&size=288x144&return_error_code=true`,
      ).then((response) => setStreetView(response.ok));
    }
  }, [position]);

  // Mobile overflow effect
  useEffect(() => {
    if (isMobile) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isMobile]);

  /* === CÓDIGO ANTIGO: useEffects do vídeo embutido (agora navega para /camera/:deviceId) ===
  useEffect(() => {
    if (!showVideo) { setVideoReady(false); }
  }, [showVideo]);

  useEffect(() => {
    // WebRTC connection setup/cleanup...
    startWebRTCStream(device.uniqueId, channel, videoElement)...
  }, [device, channel, showVideo, videoReady, retry]);
  === FIM === */

  if (!device) {
    return null;
  }

  return (
    <>
      <div className={useExternalPosition ? classes.rootExternal : classes.root}>
        <Rnd
          default={{ x: 0, y: 0, width: 'auto', height: 'auto' }}
          enableResizing={false}
          dragHandleClassName="draggable-header"
          style={{ position: 'relative' }}
          bounds={false}
          cancel=".no-drag"
        >
          <Card elevation={3} className={classes.card}>
            {(deviceImage || (position && streetView)) ? (
              <CardMedia
                image={deviceImage ? `/api/media/${device.uniqueId}/${deviceImage}` : `https://street-view.entrack-plataforma.workers.dev/?heading=${position?.course || 0}&location=${position?.latitude},${position?.longitude}&size=288x144&return_error_code=true`}
                classes={{ root: classes.media }}
                // ✅ CORREÇÃO 3: onClick no CardMedia para Street View
                onClick={handleStreetViewClick}
              >
                {/* Região transparente para arrastar - topo do card */}
                <div 
                  className="draggable-header" 
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '40px',
                    zIndex: 10,
                    cursor: 'move',
                  }}
                  onClick={(e) => e.stopPropagation()} // Impede clique de propagar para CardMedia
                />
                
                {/* Botão de fechar */}
                <IconButton
                  className={classes.imageCloseButton}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); // Impede clique de propagar para CardMedia
                    onClose();
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                >
                  <CloseIcon fontSize="small" sx={{ color: 'white' }} />
                </IconButton>

                {/* 2ECOMP Premium: Header com nome e grupo */}
                <div className={classes.imageHeader}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'white',
                      fontFamily: typography.fontFamily.heading,
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      letterSpacing: '0.02em',
                    }}
                  >
                    {device.name.toUpperCase()}
                  </Typography>
                  {hasValidGroup && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255,255,255,0.8)',
                        fontFamily: typography.fontFamily.mono,
                        fontSize: '0.72rem',
                        marginTop: '2px',
                      }}
                    >
                      {groups[device.groupId].name}
                    </Typography>
                  )}
                </div>
              </CardMedia>
            ) : (
              <div className={`${classes.header} draggable-header`}>
                <Typography variant="body2" color="textSecondary">
                  {device.name}
                </Typography>
                <IconButton size="small" onClick={onClose} onTouchStart={onClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            )}

            {/* === CÓDIGO ANTIGO COMENTADO: Video Player embutido no StatusCard ===
               Agora o botão de câmera navega para /camera/:deviceId

            {showVideo && (
              <div className={classes.videoOverlay}>
                <Box className="draggable-header" sx={{...}}>
                  <Typography>Canal {channel}</Typography>
                  <IconButton onClick={() => setShowVideo(false)}>
                    <CloseIcon />
                  </IconButton>
                </Box>
                <UnifiedVideoPlayer
                  deviceId={device.id}
                  deviceImei={device.uniqueId}
                  channel={channel}
                  videoBackend={device.attributes?.videoBackend || VIDEO_BACKEND.AUTO}
                />
                <Box>{availableChannels.map(...)}</Box>
              </div>
            )}

            === FIM DO CÓDIGO ANTIGO === */}

            {position && (
              <CardContent className={classes.content}>
                {/* Event Badge - Exibe tipo do evento quando disponível */}
                {event && eventLabel && (
                  <Box className={classes.eventBadge}>
                    <Chip
                      icon={<NotificationsActiveIcon sx={{ fontSize: 16 }} />}
                      label={eventLabel}
                      color="error"
                      size="small"
                      className={classes.eventChip}
                      sx={{
                        bgcolor: tokens.status.alarm,
                        color: tokens.text.inverse,
                        fontSize: '0.75rem',
                        height: 28,
                        '& .MuiChip-icon': {
                          color: tokens.text.inverse,
                        },
                      }}
                    />
                  </Box>
                )}
                <Table size="small" classes={{ root: classes.table }}>
                  <TableBody>
                    {positionItems.split(',').filter((key) => position.hasOwnProperty(key) || position.attributes?.hasOwnProperty(key)).map((key) => (
                      <StatusRow
                        key={key}
                        name={positionAttributes[key]?.name || key}
                        tokens={tokens}
                        content={(
                          <PositionValue
                            position={position}
                            property={position.hasOwnProperty(key) ? key : null}
                            attribute={position.hasOwnProperty(key) ? null : key}
                          />
                        )}
                      />
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            )}
            <CardActions classes={{ root: classes.actions }} disableSpacing>
              <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-around' }}>
                <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                  <Tooltip title={t('sharedExtra')}>
                    <span>
                      <IconButton
                        color="secondary"
                        onClick={(e) => setAnchorEl(e.currentTarget)}
                        disabled={!position}
                      >
                        <PendingIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
                {!disableActions && position && (
                  <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title={t('reportReplay')}>
                      <span>
                        <IconButton onClick={() => navigate(`/replay?deviceId=${deviceId}`)} disabled={!position}>
                          <RouteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                )}
                {hasCamera && (
                  <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title={t('camera') || 'Câmeras'}>
                      <span>
                        <IconButton onClick={handleCameraClick}>
                          <CameraAltIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                )}
                {(isAdmin || isManager) && (
                  <>
                    <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                      <Tooltip title={t('commandTitle')}>
                        <span>
                          <IconButton onClick={() => navigate(`/settings/device/${deviceId}/command`)} disabled={disableActions}>
                            <SendIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                    <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                      <Tooltip title={t('sharedEdit')}>
                        <span>
                          <IconButton onClick={() => navigate(`/settings/device/${deviceId}`)} disabled={disableActions || deviceReadonly}>
                            <EditIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </>
                )}
                {limitCommands && !isAdmin && !isManager && (
                  <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title={commandLoading ? 'Enviando comando...' : isBlocked ? t('sharedUnlock') : t('sharedLock')}>
                      <span>
                        <IconButton
                          onClick={handleLockUnlockClick}
                          disabled={disableActions || deviceReadonly || commandLoading}
                          sx={{ opacity: commandLoading ? 0.6 : 1 }}
                        >
                          {commandLoading ? (
                            <CircularProgress size={20} />
                          ) : isBlocked ? (
                            <LockIcon sx={{ color: tokens.semantic.error }} />
                          ) : (
                            <LockOpenIcon sx={{ color: tokens.semantic.success }} />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                )}
                {/* Âncora usando hasAnchor */}
                {(isAdmin || isManager || limitCommands) && (
                  <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                    <Tooltip
                      title={
                        hasAnchor
                          ? (device.attributes?.lockOnExit
                              ? 'Âncora Ativa (Bloqueia ao sair)'
                              : 'Âncora Ativa (Não bloqueia)')
                          : 'Sem Âncora Ativa'
                      }
                    >
                      <span>
                        <IconButton
                          onClick={() => {
                            if (hasAnchor) {
                              setOpenRemoveAnchorDialog(true);
                            } else {
                              setOpenAnchorDialog(true);
                            }
                          }}
                          disabled={disableActions || deviceReadonly}
                        >
                          <AnchorIcon
                            sx={{
                              color: hasAnchor
                                ? (device.attributes?.lockOnExit ? tokens.semantic.error : tokens.semantic.success)
                                : 'inherit'
                            }}
                          />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                )}
                <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                  <Tooltip title={t('sharedRemove')}>
                    <span>
                      <IconButton color="error" onClick={() => setOpenRemoveDialog(true)} disabled={disableActions || deviceReadonly}>
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </CardActions>
          </Card>
        </Rnd>
      </div>
      {position && (
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          <MenuItem onClick={() => navigate(`/position/${position.id}`)}>
            <Typography color="primary">{t('sharedShowDetails')}</Typography>
          </MenuItem>
          {!readonly && <MenuItem onClick={handleGeofence}>{t('sharedCreateGeofence')}</MenuItem>}
          <MenuItem component="a" target="_blank" href={`https://www.google.com/maps/search/?api=1&query=${position.latitude}%2C${position.longitude}`} rel="noreferrer">
            {t('linkGoogleMaps')}
          </MenuItem>
          <MenuItem component="a" target="_blank" href={`http://maps.apple.com/?ll=${position.latitude},${position.longitude}`} rel="noreferrer">
            {t('linkAppleMaps')}
          </MenuItem>
          <MenuItem component="a" target="_blank" href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${position.latitude}%2C${position.longitude}&heading=${position.course || 0}`} rel="noreferrer">
            {t('linkStreetView')}
          </MenuItem>
          {navigationAppTitle && (
            <MenuItem component="a" target="_blank" href={navigationAppLink.replace('{latitude}', position.latitude).replace('{longitude}', position.longitude)} rel="noreferrer">
              {navigationAppTitle}
            </MenuItem>
          )}
          {!shareDisabled && !user.temporary && (
            <MenuItem onClick={() => navigate(`/settings/device/${deviceId}/share`)}>
              <Typography color="secondary">{t('deviceShare')}</Typography>
            </MenuItem>
          )}
        </Menu>
      )}

      <RemoveDialog open={openRemoveDialog} endpoint="devices" itemId={deviceId} onResult={handleRemove} />

      <MultiActionDialog open={dialogOpen} actionType={dialogType} itemId={deviceId} onResult={handleDialogResult} />

      <AnchorDialog
        open={openAnchorDialog}
        onClose={() => setOpenAnchorDialog(false)}
        deviceName={device?.name || 'Desconhecido'}
        position={position}
        device={device}
        dispatch={dispatch}
      />

      <RemoveAnchorDialog
        open={openRemoveAnchorDialog}
        onClose={() => setOpenRemoveAnchorDialog(false)}
        deviceName={device?.name || 'Desconhecido'}
        device={device}
        position={position}
        dispatch={dispatch}
      />

      <LockUnlockConfirmationDialog
        open={openLockUnlockDialog}
        onClose={() => setOpenLockUnlockDialog(false)}
        onConfirm={executeLockUnlock}
        isBlocked={isBlocked}
        deviceName={device?.name || 'Desconhecido'}
        loading={commandLoading}
      />

      <SuccessToast
        open={showSuccessToast}
        message={toastMessage}
        severity={toastSeverity}
        onClose={() => {
          setShowSuccessToast(false);
          setToastOpen(false);
        }}
      />
    </>
  );
};

export default StatusCard;
